import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import CanonicalTag from "@/components/CanonicalTag";

const TransitionalADUDesignFremont = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Transitional ADU Design & Construction in Fremont",
    "description": "Custom Transitional ADUs in Fremont. Expert design & construction by UDA ADU. Perfect blend of traditional and contemporary styles.",
    "provider": {
      "@type": "Organization",
      "name": "UDA ADU"
    },
    "url": "https://www.udaadu.com/transitional-adu-design-fremont"
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/transitional-adu-design-fremont" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      
      <Navigation />
      
      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl h-[80vh] relative">
            <button
              onClick={closeCalendlyModal}
              className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100"
            >
              <X className="h-6 w-6" />
            </button>
            <iframe
              src="https://calendly.com/udaadu/30min"
              width="100%"
              height="100%"
              frameBorder="0"
              className="rounded-lg"
            ></iframe>
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Transitional ADU Design & Construction in Fremont
          </h1>
          <p className="text-xl text-gray-200 max-w-4xl mx-auto mb-8">
            Discover the perfect balance of traditional warmth and contemporary sophistication with a Transitional ADU in Fremont. Our designs seamlessly blend classic and modern elements for timeless appeal that fits any property style.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={openCalendlyModal} size="lg" className="text-lg px-8 py-3">
              Schedule Design Consultation
            </Button>
            <Button asChild variant="outline" size="lg" className="text-lg px-8 py-3">
              <Link to="/landing">Try UDA AI Designer</Link>
            </Button>
          </div>
        </div>

        {/* Features Section */}
        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <div>
            <h2 className="text-3xl font-semibold mb-6 text-foreground">
              Transitional ADU Design Elements
            </h2>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-foreground">Mixed Materials</h3>
                  <p className="text-muted-foreground">Sophisticated blend of stone, wood, and modern siding materials</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-foreground">Neutral Color Palettes</h3>
                  <p className="text-muted-foreground">Timeless grays, whites, and earth tones for lasting appeal</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-foreground">Clean Lines with Soft Curves</h3>
                  <p className="text-muted-foreground">Balanced geometry that's neither too formal nor too casual</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-foreground">Updated Traditional Windows</h3>
                  <p className="text-muted-foreground">Classic proportions with modern efficiency and style</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-foreground">Flexible Rooflines</h3>
                  <p className="text-muted-foreground">Varied pitches and materials for visual interest</p>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <h2 className="text-3xl font-semibold mb-6 text-foreground">
              Why Transitional ADUs Work in Fremont
            </h2>
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-foreground mb-2">Universal Appeal</h3>
                <p className="text-muted-foreground">Transitional style complements virtually any main house architecture in Fremont</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-foreground mb-2">Investment Security</h3>
                <p className="text-muted-foreground">Timeless design ensures your ADU won't look dated in years to come</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-foreground mb-2">Flexible Functionality</h3>
                <p className="text-muted-foreground">Adaptable spaces work for any use - family, guests, or rental income</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-foreground mb-2">Broad Market Appeal</h3>
                <p className="text-muted-foreground">Appeals to diverse tastes, maximizing rental and resale potential</p>
              </div>
            </div>
          </div>
        </div>

        {/* Interior Features */}
        <div className="mb-16">
          <h2 className="text-3xl font-semibold text-center mb-12 text-foreground">
            Transitional Interior Design Elements
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-muted rounded-lg">
              <h3 className="font-semibold mb-4 text-foreground">Updated Traditional Cabinetry</h3>
              <p className="text-muted-foreground">Classic Shaker doors with contemporary hardware and finishes</p>
            </div>
            <div className="text-center p-6 bg-muted rounded-lg">
              <h3 className="font-semibold mb-4 text-foreground">Natural Materials</h3>
              <p className="text-muted-foreground">Stone, wood, and marble with clean, modern applications</p>
            </div>
            <div className="text-center p-6 bg-muted rounded-lg">
              <h3 className="font-semibold mb-4 text-foreground">Balanced Lighting</h3>
              <p className="text-muted-foreground">Mix of traditional and contemporary fixtures for layered illumination</p>
            </div>
            <div className="text-center p-6 bg-muted rounded-lg">
              <h3 className="font-semibold mb-4 text-foreground">Comfortable Furnishings</h3>
              <p className="text-muted-foreground">Upholstered pieces that are both stylish and livable</p>
            </div>
            <div className="text-center p-6 bg-muted rounded-lg">
              <h3 className="font-semibold mb-4 text-foreground">Sophisticated Finishes</h3>
              <p className="text-muted-foreground">Quality materials and craftsmanship without ostentation</p>
            </div>
            <div className="text-center p-6 bg-muted rounded-lg">
              <h3 className="font-semibold mb-4 text-foreground">Open Yet Defined Spaces</h3>
              <p className="text-muted-foreground">Flowing layouts with subtle zone definitions</p>
            </div>
          </div>
        </div>

        {/* Adaptability Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-semibold text-center mb-12 text-foreground">
            Transitional ADU Versatility
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-6 bg-muted rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Multi-Generational Living</h3>
              <p className="text-muted-foreground mb-4">Create comfortable, dignified living spaces for aging parents or adult children returning home.</p>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Accessible design options available</li>
                <li>• Private yet connected to main house</li>
                <li>• Familiar, comfortable aesthetic</li>
              </ul>
            </div>
            <div className="p-6 bg-muted rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Premium Rental Income</h3>
              <p className="text-muted-foreground mb-4">Attractive to quality tenants seeking sophisticated, well-designed living spaces.</p>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Appeals to professional tenants</li>
                <li>• Commands higher rental rates</li>
                <li>• Lower tenant turnover</li>
              </ul>
            </div>
            <div className="p-6 bg-muted rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Home Office & Studio</h3>
              <p className="text-muted-foreground mb-4">Perfect environment for focused work or creative pursuits.</p>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Professional appearance for client meetings</li>
                <li>• Quiet, separate workspace</li>
                <li>• Flexible layout options</li>
              </ul>
            </div>
            <div className="p-6 bg-muted rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Guest Accommodation</h3>
              <p className="text-muted-foreground mb-4">Elegant guest quarters that make visitors feel welcomed and comfortable.</p>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Hotel-quality comfort</li>
                <li>• Privacy for extended stays</li>
                <li>• Sophisticated hospitality</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Process Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-semibold text-center mb-12 text-foreground">
            Fremont Transitional ADU Design Process
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">1</span>
              </div>
              <h3 className="font-semibold mb-2 text-foreground">Style Consultation</h3>
              <p className="text-muted-foreground">Explore transitional design options for your Fremont property</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">2</span>
              </div>
              <h3 className="font-semibold mb-2 text-foreground">Balanced Design</h3>
              <p className="text-muted-foreground">Create harmonious plans blending traditional and modern elements</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">3</span>
              </div>
              <h3 className="font-semibold mb-2 text-foreground">Fremont Approvals</h3>
              <p className="text-muted-foreground">Navigate local building requirements and design review</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">4</span>
              </div>
              <h3 className="font-semibold mb-2 text-foreground">Quality Construction</h3>
              <p className="text-muted-foreground">Expert craftsmanship ensuring lasting beauty and function</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-muted rounded-lg p-8 text-center">
          <h2 className="text-3xl font-semibold mb-4 text-foreground">
            Create Your Perfect Transitional ADU in Fremont
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Design a beautifully balanced Transitional ADU that combines the best of traditional and contemporary styles. Our Fremont experts will create a timeless space that enhances your property value and lifestyle.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={openCalendlyModal} size="lg" className="text-lg px-8 py-3">
              Begin Your Transitional Project
            </Button>
            <Button asChild variant="outline" size="lg" className="text-lg px-8 py-3">
              <Link to="/adu-calculator">Get Design Estimate</Link>
            </Button>
          </div>
        </div>

        {/* Related Links */}
        <div className="mt-16 text-center">
          <h3 className="text-xl font-semibold mb-4 text-foreground">Explore More Fremont ADU Designs</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contemporary-adu-design-fremont" className="text-[#FF7A00] hover:underline">
              Contemporary ADUs Fremont
            </Link>
            <Link to="/modern-farmhouse-adu-design-fremont" className="text-[#FF7A00] hover:underline">
              Modern Farmhouse ADUs Fremont
            </Link>
            <Link to="/craftsman-adu-design-fremont" className="text-[#FF7A00] hover:underline">
              Craftsman ADUs Fremont
            </Link>
            <Link to="/spanish-colonial-adu-design-fremont" className="text-[#FF7A00] hover:underline">
              Spanish Colonial ADUs Fremont
            </Link>
          </div>
        </div>
      </main>

      <Footer />
      <FloatingADUWizard />
      <FloatingReserveButton />
    </div>
  );
};

export default TransitionalADUDesignFremont;